import {
    ActionButton,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
} from "@jhool-io/fe-components";
import React from "react";
import { useSearchParams } from "react-router-dom";
import moment from "../../../../../utils/moment";
import { useFetchClientList } from "../../../../../hooks/queries/client";
import { UserRole } from "../../../../user-management/types/user-management.types";
import TableFiltersDisplay from "../../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import ScheduleWeekView from "../ScheduleWeekView/ScheduleWeekView";
import {
    displayNameInRightFormat,
    isSmallScreen,
} from "../../../../../utils/helpers";

import { useAppSelector } from "../../../../../hooks/useRedux";
import ChevronLeftIcon from "../../../../../icons/ChevronLeft";
import ChevronRightIcon from "../../../../../icons/ChevronRight";

export default function ScheduleViewWrapper() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentWeek, setCurrentWeek] = React.useState(
        moment().startOf("week")
    );
    const { practice } = useAppSelector((state) => state.userPractice);

    const handleSetProviderId = () => {
        if (
            !practice?.roles?.includes(UserRole.AUDITOR) &&
            practice?.roles?.includes(UserRole.PROVIDER)
        ) {
            return practice?.provider_id;
        }

        return "";
    };
    const clientFilter = searchParams.getAll("client_ids[]") || [];

    const { data } = useFetchClientList({
        page: 1,
        provider_id: handleSetProviderId() as string,
    });

    // Function to handle week navigation
    const updateWeekRange = (direction: string) => {
        const newWeek =
            direction === "next"
                ? moment(currentWeek).add(1, "week")
                : moment(currentWeek).subtract(1, "week");
        searchParams.set(
            "from_date",
            newWeek.startOf("week").format("YYYY-MM-DD")
        );
        searchParams.set("to_date", newWeek.endOf("week").format("YYYY-MM-DD"));
        setSearchParams(searchParams);
        setCurrentWeek(newWeek);
    };

    const handleClientFilter = (client_id: string) => {
        if (clientFilter.includes(client_id)) {
            searchParams.delete("client_ids[]", client_id);
        } else {
            searchParams.append("client_ids[]", client_id);
        }
        setSearchParams(searchParams);
    };

    const startOfWeek = currentWeek.startOf("week").format("MMM D");
    const endOfWeek = currentWeek.endOf("week").format("MMM D, YYYY");

    const currentWeekRange = `${startOfWeek} - ${endOfWeek}`;
    const filteredClientNames = data?.data
        .filter((client) => clientFilter.includes(client.client_id))
        .map((client) =>
            displayNameInRightFormat({
                firstName: client.first_name,
                lastName: client.last_name,
            })
        );

    return (
        <div className="relative">
            <Card classNames="h-fit sticky top-6 z-[4] flex items-center gap-x-[--spacing-items]">
                <div>
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>Client</FilterButton>
                        </DropdownTrigger>
                        <DropdownContent
                            align={isSmallScreen() ? "start" : "end"}
                            width="auto"
                        >
                            {data?.data?.map((client) => (
                                <DropdownItem
                                    key={client.client_id}
                                    onClick={() =>
                                        handleClientFilter(client.client_id)
                                    }
                                    isMulti
                                    itemValue={client.client_id}
                                    values={clientFilter}
                                    closeMenuOnSelect
                                >
                                    {displayNameInRightFormat({
                                        firstName: client.first_name,
                                        lastName: client.last_name,
                                    })}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "client_ids[]",
                                values: filteredClientNames as string[],
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                    />
                </div>

                <div className="ml-auto">
                    <div className="flex items-center gap-x-[--spacing-items] text-base font-medium">
                        <ActionButton
                            variant="can-hover"
                            size="auto"
                            onClick={() => updateWeekRange("previous")}
                            className="rounded-full [border:1px_solid_var(--color-stroke)] size-7 p-0"
                            actionTitle="Previous week"
                        >
                            <ChevronLeftIcon className="stroke-text" />
                        </ActionButton>
                        {currentWeekRange}
                        <ActionButton
                            variant="can-hover"
                            size="auto"
                            className="rounded-full [border:1px_solid_var(--color-stroke)] size-7 p-0"
                            actionTitle="Next week"
                            onClick={() => updateWeekRange("next")}
                        >
                            <ChevronRightIcon className="stroke-text" />
                        </ActionButton>
                    </div>
                </div>
            </Card>
            <div className="mt-[--spacing-header]">
                <ScheduleWeekView />
            </div>
        </div>
    );
}
